import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { z as zod } from 'zod';
import { Button, Col, Container, Row, Form } from 'shared-portal-component';

import { selectDraft, selectInternalUsers } from '@/store/selectors/createSix';
import { setDraftField, setStep } from '@/store/slices/createSix/createSixSlice';
import { useAppDispatch } from '@/store';
import { fetchCreateSixModalInformation } from '@/store/thunks/create-six/fetchCreateSixModalInformation';
import { selectUser } from '@/store/selectors/user';
import { MIN_LEN } from '@/constants';

const StepOwners: React.FC = () => {
  const dispatch = useAppDispatch();
  const draft = useSelector(selectDraft);
  const users = useSelector(selectInternalUsers);
  const currentUser = useSelector(selectUser);

  useEffect(() => {
    void dispatch(fetchCreateSixModalInformation());
  }, [dispatch]);

  // Filter out users who were already chosen as internal participants
  const availableOwners = users.filter(
    (user) => !draft.internalParticipantIds.includes(user.id)
  );

  const MIN = 1;
  const schema = zod.object({
    ownerIds: zod.array(zod.string()).min(MIN, 'Select at least one owner'),
  });

  const fields = [
    {
      name: 'ownerIds',
      label: 'Owners',
      type: 'multiselect',
      required: true,
      items: availableOwners.map((user) => ({
        label: user.name,
        value: user.id,
        email: user.email,
      })),
    },
  ];

  const handleSubmit = (values: { ownerIds: string[] }) => {
    dispatch(setDraftField({ key: 'ownerIds', value: values.ownerIds }));
    dispatch(setStep('InternalParticipants'));
  };

  useEffect(() => {
  if (draft.ownerIds.length > MIN_LEN) return;
  if ((currentUser?.id) === undefined) return;

  dispatch(setDraftField({ key: 'ownerIds', value: [currentUser.id] }));
}, [dispatch, currentUser?.id, draft.ownerIds.length]);

  const handleBack = () => dispatch(setStep('Description'));

  return (
    <Container fluid>
      <Row spacing="md" justify="center">
        <Col md={7} sm={10} xs={12}>
          <Form
            schema={schema}
            fields={fields}
            onSubmit={handleSubmit}
            defaultValues={{ ownerIds: draft.ownerIds }}
            secondaryButton={
              <Button
                text="Go Back"
                variant="primary"
                onClick={handleBack}
                size="md"
                className="form-button"
              />
            }
          />
        </Col>
      </Row>
    </Container>
  );
};

export default StepOwners;
